import resourceStrings from '../../ResourceStrings';
import { BarGroup } from './BarGroup';
import { BaseCommand, CommandDefinition } from './commands';
import { VodaBars } from './VodaBars';

type DefinitionAliasType = 'voda-detailed-bar' | 'voda-detailed-unbar';

export interface VodaDetailedBarUnbarSettings extends VodaBars<boolean> {
  definitionAlias: DefinitionAliasType;
}

export class VodaDetailedBarUnbarCommand implements VodaDetailedBarUnbarSettings {
  public admin: boolean = true;
  public arkessaApn: boolean = false;
  public conferenceCalling: boolean = false;
  public definitionAlias: DefinitionAliasType;
  public gprs: boolean = false;
  public gprsRoaming: boolean = false;
  public voiceRoaming: boolean = false;
  public outgoing: boolean = false;
  public international: boolean = false;
  public premium: boolean = false;
  public smsIncoming: boolean = false;
  public smsOutgoing: boolean = false;
  public smsPremium: boolean = false;
  public mms: boolean = false;
  public wifiCalling: boolean = false;
  public enable5G: boolean = false;
  public intxHome: boolean = false;

  public definition: CommandDefinition;
  public confirm: boolean;
  public key: string;
  public accountId: number | null = null;
  public networkId: number | null = null;
  public showValidationErrors: boolean;
  public stockIds: number[]; // selected SIMs within command

  constructor(command: BaseCommand) {
    this.definition = command.definition;
    this.key = command.definition.jsonAlias;
    this.confirm = command.confirm;
    this.showValidationErrors = command.showValidationErrors;
    this.stockIds = command.stockIds;
    this.definitionAlias = command.definition.jsonAlias as DefinitionAliasType;
  }

  public isValid() {
    return (
      this.admin ||
      this.gprs ||
      this.gprsRoaming ||
      this.voiceRoaming ||
      this.outgoing ||
      this.international ||
      this.premium ||
      this.smsIncoming ||
      this.smsOutgoing ||
      this.smsPremium ||
      this.mms ||
      this.conferenceCalling ||
      this.wifiCalling ||
      this.arkessaApn ||
      this.enable5G ||
      this.intxHome
    );
  }

  public hasAdvancedOptionsSelected() {
    return false;
  }
}

export function getVodaDetailedBarUnbarOptions(
  props: VodaDetailedBarUnbarSettings,
  companyId: number | undefined,
  includeSuffixes: boolean,
) {
  const { definitionAlias } = props;
  let barSuffix: string = '';
  let featureSuffix: string = '';
  if (includeSuffixes) {
    if (props.definitionAlias === 'voda-detailed-bar') {
      barSuffix = ' (+)';
      featureSuffix = ' (-)';
    } else {
      barSuffix = ' (-)';
      featureSuffix = ' (+)';
    }
  }

  const includeArkessa = companyId === 442492 && definitionAlias === 'voda-detailed-unbar';
  return getVodaDetailedBarUnbarOptionsV2<boolean>(includeArkessa, barSuffix, featureSuffix, props);
}

export function getVodaDetailedBarUnbarOptionsV2<T>(
  includeArkessa: boolean,
  barSuffix: string,
  featureSuffix: string,
  props: VodaBars<T>,
) {
  const {
    admin,
    arkessaApn,
    conferenceCalling,
    gprs,
    gprsRoaming,
    international,
    outgoing,
    mms,
    premium,
    smsIncoming,
    smsOutgoing,
    smsPremium,
    voiceRoaming,
    wifiCalling,
    enable5G,
    intxHome,
  } = props;
  const options = [
    {
      label: `Admin${barSuffix}`,
      name: 'admin',
      checked: admin,
      isAdvanced: false,
    },
    {
      label: `Domestic Data${barSuffix}`,
      name: 'gprs',
      checked: gprs,
      isAdvanced: true,
    },
    {
      label: `Roaming Data${barSuffix}`,
      name: 'gprsRoaming',
      checked: gprsRoaming,
      isAdvanced: true,
    },
    {
      label: `Voice Roaming${barSuffix}`,
      name: 'voiceRoaming',
      checked: voiceRoaming,
      isAdvanced: true,
    },
    {
      label: `Outgoing Calls${barSuffix}`,
      name: 'outgoing',
      checked: outgoing,
      isAdvanced: true,
    },
    {
      label: `International Calls${barSuffix}`,
      name: 'international',
      checked: international,
      isAdvanced: true,
    },
    {
      label: `Premium Calls${barSuffix}`,
      name: 'premium',
      checked: premium,
      isAdvanced: true,
    },
    {
      label: `Incoming SMS${barSuffix}`,
      name: 'smsIncoming',
      checked: smsIncoming,
      isAdvanced: true,
    },
    {
      label: `Outgoing SMS${barSuffix}`,
      name: 'smsOutgoing',
      checked: smsOutgoing,
      isAdvanced: true,
    },
    {
      label: `Premium SMS${barSuffix}`,
      name: 'smsPremium',
      checked: smsPremium,
      isAdvanced: true,
    },
    {
      label: `MMS${featureSuffix}`,
      name: 'mms',
      checked: mms,
      isAdvanced: true,
    },
    {
      label: `Conference Calling ${featureSuffix}`,
      name: 'conferenceCalling',
      checked: conferenceCalling,
      isAdvanced: true,
    },
    {
      label: `Wifi Calling ${featureSuffix}`,
      name: 'wifiCalling',
      checked: wifiCalling,
      isAdvanced: true,
    },
    {
      label: `5G ${featureSuffix}`,
      name: 'enable5G',
      checked: enable5G,
      isAdvanced: true,
    },
    {
      label: `International Calls (ex. UK) ${barSuffix}`,
      name: 'intxHome',
      checked: intxHome,
      isAdvanced: true,
    },
  ];

  if (includeArkessa) {
    options.push({
      label: 'Arkessa APN',
      name: 'arkessaApn',
      checked: arkessaApn,
      isAdvanced: true,
    });
  }

  return options;
}

export function getVodaDetailedBarUnbarOptionGroups<T>(
  includeArkessa: boolean,
  barSuffix: string,
  featureSuffix: string,
  props: VodaBars<T>,
) {
  const {
    admin,
    arkessaApn,
    conferenceCalling,
    gprs,
    gprsRoaming,
    international,
    outgoing,
    mms,
    premium,
    smsIncoming,
    smsOutgoing,
    smsPremium,
    voiceRoaming,
    wifiCalling,
    intxHome,
  } = props;
  const options: BarGroup<T>[] = [
    {
      group: resourceStrings.main,
      items: [
        {
          label: `Admin${barSuffix}`,
          name: 'admin',
          checked: admin,
          isAdvanced: false,
        },
      ],
    },
    {
      group: resourceStrings.calls,
      items: [
        {
          label: `Voice Roaming${barSuffix}`,
          name: 'voiceRoaming',
          checked: voiceRoaming,
          isAdvanced: true,
        },
        {
          label: `Outgoing Calls${barSuffix}`,
          name: 'outgoing',
          checked: outgoing,
          isAdvanced: true,
        },
        {
          label: `International Calls${barSuffix}`,
          name: 'international',
          checked: international,
          isAdvanced: true,
        },
        {
          label: `Premium Calls${barSuffix}`,
          name: 'premium',
          checked: premium,
          isAdvanced: true,
        },
        {
          label: `Conference Calling ${featureSuffix}`,
          name: 'conferenceCalling',
          checked: conferenceCalling,
          isAdvanced: true,
        },
        {
          label: `Wifi Calling ${featureSuffix}`,
          name: 'wifiCalling',
          checked: wifiCalling,
          isAdvanced: true,
        },
        {
          label: `International Calls (ex. UK) ${featureSuffix}`,
          name: 'intxHome',
          checked: intxHome,
          isAdvanced: true,
        },
      ],
    },
    {
      group: resourceStrings.sms,
      items: [
        {
          label: `Incoming SMS${barSuffix}`,
          name: 'smsIncoming',
          checked: smsIncoming,
          isAdvanced: true,
        },
        {
          label: `Outgoing SMS${barSuffix}`,
          name: 'smsOutgoing',
          checked: smsOutgoing,
          isAdvanced: true,
        },
        {
          label: `Premium SMS${barSuffix}`,
          name: 'smsPremium',
          checked: smsPremium,
          isAdvanced: true,
        },
        {
          label: `MMS${featureSuffix}`,
          name: 'mms',
          checked: mms,
          isAdvanced: true,
        },
      ],
    },
    {
      group: resourceStrings.data,
      items: [
        {
          label: `Domestic Data${barSuffix}`,
          name: 'gprs',
          checked: gprs,
          isAdvanced: true,
        },
        {
          label: `Roaming Data${barSuffix}`,
          name: 'gprsRoaming',
          checked: gprsRoaming,
          isAdvanced: true,
        },
      ],
    },
  ];

  if (includeArkessa) {
    options.push({
      group: resourceStrings.misc,
      items: [
        {
          label: 'Arkessa APN',
          name: 'arkessaApn',
          checked: arkessaApn,
          isAdvanced: true,
        },
      ],
    });
  }

  return options;
}
